import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Issue, EmergencyContact, DashboardStats, MaintenanceSchedule } from '../lib/api';
import { useDashboard, useUpcomingMaintenance } from '../hooks/useApiQueries';
import { 
  WarningCircle, 
  CheckCircle, 
  Clock, 
  Plus,
  ArrowRight,
  Phone,
  Siren,
  Wrench,
  CalendarCheck,
  Warning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import ReminderWidget from '../components/ReminderWidget';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  
  // React Query hooks för all data
  const { data: dashboardData, isLoading: dashboardLoading } = useDashboard(user?.organization_id, !!user?.organization_id);
  const { data: upcomingMaintenanceData = [] } = useUpcomingMaintenance(
    user?.organization_id, 
    30, 
    isAdmin && !!user?.organization_id
  );
  
  // Extrahera data från dashboard response
  const stats: DashboardStats | null = isAdmin && dashboardData ? dashboardData.stats : null;
  const recentIssues: Issue[] = dashboardData?.recentIssues || [];
  const emergencyContacts: EmergencyContact[] = dashboardData?.emergencyContacts || [];
  const upcomingMaintenance: MaintenanceSchedule[] = upcomingMaintenanceData.slice(0, 5);
  
  const loading = dashboardLoading;

  const getStatusBadge = (status: string | undefined): string => {
    const badges: Record<string, string> = {
      open: 'badge-neutral',
      in_progress: 'badge-warning',
      resolved: 'badge-success',
      closed: 'badge-neutral'
    };
    return status ? (badges[status] || 'badge-neutral') : 'badge-neutral';
  };

  const getUrgencyLabel = (level: number | undefined): string => {
    const labels: Record<number, string> = {
      1: t('urgency_1'),
      2: t('urgency_2'),
      3: t('urgency_3'),
      4: t('urgency_4'),
      5: t('urgency_5')
    };
    return level ? (labels[level] || String(level)) : '';
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t('dashboard')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('welcome')}, {user?.name}
          </p>
        </div>
      </div>

      {/* Admin Stats */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                <WarningCircle size={20} className="text-slate-600" />
              </div>
              <div>
                <div className="stat-value">{stats.issues.total}</div>
                <div className="stat-label">{t('total_issues')}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-open">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                <Clock size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="stat-value">{stats.issues.open}</div>
                <div className="stat-label">{t('open_issues')}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-emergency">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-red-50 flex items-center justify-center">
                <Siren size={20} className="text-red-600" />
              </div>
              <div>
                <div className="stat-value">{stats.issues.emergency}</div>
                <div className="stat-label">{t('emergency_issues')}</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-resolved">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-green-50 flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <div className="stat-value">{stats.issues.resolved}</div>
                <div className="stat-label">{t('resolved_issues')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Issues */}
        <div className="lg:col-span-2">
          <div className="card-flat">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-medium text-slate-900">
                {isAdmin ? t('all_issues') : t('my_issues')}
              </h2>
              <Link to="/issues" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
                {t('all_issues')}
                <ArrowRight size={14} />
              </Link>
            </div>
            
            {recentIssues.length === 0 ? (
              <div className="empty-state">
                <WarningCircle size={48} className="empty-state-icon" />
                <p>{t('no_results')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentIssues.map((issue) => (
                  <Link
                    key={issue.id}
                    to={`/issues/${issue.id}`}
                    className={`issue-card block ${(issue.urgency_level ?? 0) >= 4 ? 'issue-card-emergency' : ''}`}
                    data-testid={`issue-card-${issue.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-slate-900 truncate">
                          {issue.title}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {issue.description}
                        </p>
                      </div>
                      <span className={getStatusBadge(issue.status)}>
                        {issue.status ? t(issue.status) : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span>{issue.category ? t(issue.category) : ''}</span>
                      <span>•</span>
                      <span>{getUrgencyLabel(issue.urgency_level)}</span>
                      <span>•</span>
                      <span>{issue.created_at ? new Date(issue.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Emergency Contacts */}
        <div>
          <div className="card-flat">
            <h2 className="text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Siren size={18} className="text-red-500" />
              {t('emergency_contacts')}
            </h2>
            
            <p className="text-xs text-slate-500 mb-4">
              {t('emergency_warning')}
            </p>
            
            <div className="space-y-3">
              {emergencyContacts.map((contact) => (
                <div
                  key={contact.id || contact.phone}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-md"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {contact.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {contact.description}
                    </div>
                  </div>
                  <a
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-1 text-sm font-mono text-slate-700 hover:text-slate-900"
                    data-testid={`emergency-phone-${contact.phone}`}
                  >
                    <Phone size={14} />
                    {contact.phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Maintenance Widget - Admin only */}
      {isAdmin && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Reminder Widget */}
          <ReminderWidget schedules={upcomingMaintenance} />
          
          {/* Maintenance List */}
          {upcomingMaintenance.length > 0 && (
            <div className="card-flat" data-testid="maintenance-widget">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-medium text-slate-900 flex items-center gap-2">
                  <CalendarCheck size={18} className="text-amber-600" />
                  Kommande underhåll
                </h2>
                <Link to="/maintenance" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  Visa alla
                  <ArrowRight size={14} />
                </Link>
              </div>
              
              <div className="space-y-2">
                {upcomingMaintenance.map((item) => {
                  const daysUntil = item.days_until ?? 0;
                  const isOverdue = daysUntil < 0;
                  const isUrgent = daysUntil >= 0 && daysUntil <= 7;
                  
                  return (
                    <div 
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isOverdue ? 'bg-red-50 border-red-200' : 
                        isUrgent ? 'bg-amber-50 border-amber-200' : 
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                          isOverdue ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-slate-100'
                        }`}>
                          {isOverdue ? (
                            <Warning size={16} className="text-red-600" />
                          ) : (
                            <Wrench size={16} className={isUrgent ? 'text-amber-600' : 'text-slate-500'} />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{item.title}</div>
                          <div className="text-xs text-slate-500">
                            {item.property_name || 'Alla fastigheter'}
                          </div>
                        </div>
                      </div>
                      <div className={`text-xs font-medium px-2 py-1 rounded ${
                        isOverdue ? 'bg-red-100 text-red-700' : 
                        isUrgent ? 'bg-amber-100 text-amber-700' : 
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {isOverdue ? `${Math.abs(daysUntil)} dagar försenad` :
                         daysUntil === 0 ? 'Idag' :
                         daysUntil === 1 ? 'Imorgon' :
                         `Om ${daysUntil} dagar`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
